/**
 * FarmersPulse — Full feature lists per tool (farm-management stage).
 *
 * These are the "what the farmer gets" bullet lists from the product
 * reference document (2026-02, user-supplied). Kept isolated from the
 * mockup data so we can update the feature copy without touching the
 * mockup content.
 *
 * Shape (each entry keyed by tool slug):
 *   {
 *     lead:   "one-sentence tagline farmers understand instantly",
 *     groups: [
 *       { label?: "Optional group heading", items: [ "feature", "feature" ] },
 *       ...
 *     ]
 *   }
 *
 * Rules for every string here:
 *   - Plain-farmer language, no jargon.
 *   - Present tense, active voice.
 *   - No invented numbers, prices or certifications.
 *   - When the source doc says "possible identification, not confirmed
 *     diagnosis" or "approval required" — keep that verbatim.
 */

export const TOOL_FEATURES = {
  "profile-website": {
    lead: "A guided setup that produces every piece of your public presence — plus dedicated shop, CSA and farm-stay pages when you're ready.",
    groups: [
      { label: "Every farm site includes", items: [
        "Verified farmer and farm profile",
        "Farm story and photographs",
        "Products and services",
        "Operating hours",
        "Farm stand and market schedule",
        "Map and directions",
        "Pickup and delivery information",
        "Phone, inquiry and WhatsApp buttons",
        "Photo and video gallery",
        "Reviews and testimonials block",
        "Translation and text-to-speech in 37 languages",
        "QR code you can print for markets and flyers",
        "Search-friendly public pages",
      ]},
      { label: "Optional dedicated pages", items: [
        "Farm shop (products with add-to-cart)",
        "CSA or subscription page",
        "Farm stand and market schedule page",
        "Farm-stay or farm-dining pages",
        "Pickup and delivery zones page",
      ]},
      { items: [
        "Publish at farmname.farmerspulse.com — connect your own domain later",
      ]},
    ],
  },

  "product-catalog": {
    lead: "Add a product in under a minute — it appears on your website, catalog, inventory, marketing and buyer-match feed.",
    groups: [
      { label: "Every product record captures", items: [
        "Photograph",
        "Product and variety",
        "Price and unit",
        "Available quantity",
        "Harvest or availability date",
        "Pickup and delivery options",
        "Retail and wholesale price",
        "Organic or certification status",
        "Live status chip — Available now · Coming soon · Sold out",
      ]},
      { label: "One record feeds", items: [
        "Farm website",
        "Buyer marketplace listing",
        "Inventory ledger",
        "Marketing drafts",
        "Buyer-matching feed",
      ]},
    ],
  },

  "farm-map": {
    lead: "Draw or walk your fields — everything else on the farm attaches to what you drew.",
    groups: [
      { label: "The map connects", items: [
        "Fields and crops",
        "Planting and harvest records",
        "Pest observations",
        "Irrigation events",
        "Soil tests",
        "Weather alerts scoped to each field",
        "Tasks and follow-ups",
        "Costs and yields",
      ]},
      { items: [
        "Automatic area and perimeter (acres · hectares · cents)",
        "Clean GeoJSON export for co-ops, insurance and subsidies",
      ]},
    ],
  },

  "voice-diary": {
    lead: "Speak what happened. The assistant extracts the details and writes the record for you.",
    groups: [
      { label: "Extracted from every recording", items: [
        "Field",
        "Crop",
        "Activity",
        "Quantity",
        "Labor",
        "Input (seed, compost, water)",
        "Date and location",
        "Cost, if you mentioned one",
        "Follow-up task, if you asked for one",
      ]},
      { items: [
        "Original voice recording is preserved on every entry",
        "Works offline · syncs later when signal returns",
        "You review and edit before saving — nothing auto-commits",
      ]},
    ],
  },

  "inventory-surplus": {
    lead: "One universal ledger that tracks everything you've grown, own, or are about to lose — with automatic surplus routing when a batch is at risk.",
    groups: [
      { label: "Tracks", items: [
        "Expected harvest and expected harvest dates",
        "Harvested quantity",
        "Available quantity",
        "Reserved and sold quantity",
        "Multi-location storage (packing shed · cold room · barn)",
        "Best-before date and shelf-life countdowns",
        "Damaged, spoiled or lost quantity",
        "Surplus at risk",
        "Donations logged",
        "Discounts applied",
        "QR labels for every crate and box",
        "Revenue-per-crop reporting",
      ]},
      { label: "Triggers when a batch is at risk", items: [
        "Suggested markdown at a break-even-safe price",
        "Bulk offer to restaurants and wholesale buyers",
        "Donation matching to a nearby food bank",
        "Processing partner referral (preserves, sauces)",
        "Animal-feed swap with a neighbouring farm",
        "Auto-publish surplus to FarmersPulse · FoodPulse · SellnBuy",
      ]},
    ],
  },

  "weather-to-action": {
    lead: "Twelve rules turn the forecast into a plain-language do-this-today card.",
    groups: [
      { label: "Rules that fire when the signal is present", items: [
        "Best irrigation window",
        "Heavy-rain preparation",
        "Frost protection",
        "Unsafe spraying conditions",
        "Heat stress",
        "Harvest-before-rain suggestion",
        "Livestock precautions",
        "Pest-risk conditions",
        "Soil-workability warning",
        "Wind-damage warning",
        "Disease (leaf-wetness) risk",
        "Planting window opening",
      ]},
      { label: "Every card shows", items: [
        "Source (Open-Meteo · daily and hourly forecast)",
        "Data timestamp",
        "Confidence percentage",
        "The reasoning behind the recommendation",
      ]},
    ],
  },

  "crop-planning": {
    lead: "Location-aware planting calendar for 24+ crops with editable succession, yield and labor estimates.",
    groups: [
      { label: "Estimates for every crop plan", items: [
        "Seed requirements",
        "Planting and transplanting dates",
        "Plant spacing",
        "Successive plantings (batch 1 · 2 · 3)",
        "Expected harvest window",
        "Expected yield",
        "Labor requirements",
        "Estimated cost",
        "Expected customer or buyer demand",
        "Possible shortage or surplus",
      ]},
      { items: [
        "Every estimate stays editable — your local knowledge overrides the model",
        "Edits carry forward into next season's plan automatically",
      ]},
    ],
  },

  "pest-disease": {
    lead: "Collect evidence first · Read possibilities second · Act safely third. Always framed as possible identification, never confirmed diagnosis.",
    groups: [
      { label: "Every submission returns", items: [
        "Ranked possible causes",
        "Confidence level for each",
        "Supporting evidence from your photos",
        "Photographs still needed to raise confidence",
        "Scouting checklist ('leaf undersides · 10 plants across the edge')",
        "Nearby reports from other FarmersPulse growers",
        "Safe immediate precautions (no pesticide rates)",
        "Expert or extension referral",
        "Official local treatment resources",
      ]},
      { items: [
        "Every result says 'possible identification' — never 'confirmed diagnosis'",
        "You always review before any action is logged",
      ]},
    ],
  },

  "orders-delivery": {
    lead: "One clean order lifecycle from customer enquiry to reconciled receipt — plus waitlists, substitutions and deposits for when the crop isn't ready yet.",
    groups: [
      { label: "Supports", items: [
        "Customer inquiries",
        "Preorders before harvest",
        "Farm-box orders",
        "Variable-weight products (sold by weight at pickup)",
        "Pickup locations · farm collection · market collection",
        "Delivery zones and route planning",
        "Order status timeline",
        "Payment (Stripe · Razorpay · cash-on-collection · QR-only)",
        "Deposits and installment payments",
        "Coupon codes and referral credits",
        "Packing lists and QR-coded boxes",
        "Customer notifications",
        "Uncollected-order alerts",
      ]},
      { label: "For seasonal and variable crops", items: [
        "Waitlists when a product is oversubscribed",
        "Substitutions when a crop isn't available (with your approval rules)",
        "Refund and cancellation rules",
        "Pausing individual items on a subscription box",
      ]},
      { items: [
        "Every order posts a ledger entry to Farm Finance automatically",
        "Inventory decrements the moment an order is accepted",
      ]},
    ],
  },

  "ai-marketing": {
    lead: "The AI drafts. You approve. Every message is grounded in your real inventory and availability.",
    groups: [
      { label: "Draft types", items: [
        "Weekly availability newsletters",
        "Product descriptions",
        "Farmers-market reminders",
        "Social posts",
        "Seasonal promotions",
        "Festival and holiday offers",
        "Website updates and short articles",
        "Google Business Profile posts",
        "Photo captions and alt text",
        "Customer welcome messages",
        "Review requests",
        "Abandoned-cart reminders",
        "Customer win-back messages",
      ]},
      { label: "Hard rules", items: [
        "Farmer approves every draft before it is published",
        "The AI never invents prices, products, availability or certifications",
        "Every draft cites the exact farm record it used",
      ]},
    ],
  },

  "buyer-marketplace": {
    lead: "Verified buyers post what they need. Farms respond alone — or combine supply — to hit the volume.",
    groups: [
      { label: "Every buyer request captures", items: [
        "Product and variety",
        "Quantity",
        "Grade",
        "Delivery schedule",
        "Location",
        "Certification requirements",
        "Expected price or request for quotation",
      ]},
      { label: "How farms respond", items: [
        "Reply alone with your own supply",
        "Combine supply with nearby farms to hit the volume",
        "FarmersPulse assembles the combined proposal, delivery plan and payment allocation",
        "Direct contact stays private until both sides accept the terms",
      ]},
      { items: [
        "Example — a school needs 200 kg tomatoes weekly. Three farms offer 80, 70 and 60 kg. FarmersPulse assembles the combined proposal automatically.",
      ]},
    ],
  },

  "profitability": {
    lead: "Answers the only question that matters — should I grow more, grow less, change the price, or use a different sales channel?",
    groups: [
      { label: "Calculate profitability by", items: [
        "Crop",
        "Field",
        "Acre",
        "Season",
        "Customer type",
        "Farmers market",
        "Wholesale buyer",
        "CSA",
        "Online store",
      ]},
      { label: "Every cost line-item counted", items: [
        "Seeds and plants",
        "Inputs (compost · fertilizer · amendments)",
        "Labor",
        "Machinery",
        "Water and energy",
        "Packaging",
        "Storage",
        "Transportation",
        "Marketplace fees",
        "Waste and unsold products",
        "Sales revenue",
      ]},
    ],
  },
};
